import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import '../../styles/AddInquiry.css';

const EditInquiry = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    mobile_no: '',
    area: '',
    date: '',
    plan_to_join_days: '',
    reminder_date: '',
    source: 'Google',
    status: 'Open'
  });

  useEffect(() => {
    fetchInquiry();
  }, [id]);

  // Handle Escape key to go back
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleBack();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Auto-calculate reminder date when date or plan_to_join_days changes
  useEffect(() => {
    calculateReminderDate();
  }, [formData.date, formData.plan_to_join_days]);

  const fetchInquiry = async () => {
    try {
      const response = await api.get(`/inquiry/get_inquiry.php?id=${id}`);
      if (response.data.success) {
        const inquiry = response.data.data;
        setFormData({
          full_name: inquiry.full_name || '',
          mobile_no: inquiry.mobile_no || '',
          area: inquiry.area || '',
          date: inquiry.date || '',
          plan_to_join_days: inquiry.plan_to_join_days || '',
          reminder_date: inquiry.reminder_date || '',
          source: inquiry.source || 'Google',
          status: inquiry.status || 'Open'
        });
      } else {
        setError('Inquiry not found');
      }
    } catch (err) {
      setError('Failed to load inquiry');
    } finally {
      setLoading(false);
    }
  };

  const calculateReminderDate = () => {
    if (formData.date && formData.plan_to_join_days) {
      const days = parseInt(formData.plan_to_join_days);
      if (!isNaN(days) && days > 0) {
        const baseDate = new Date(formData.date);
        const reminderDate = new Date(baseDate);
        reminderDate.setDate(baseDate.getDate() + days);
        
        const formattedDate = reminderDate.toISOString().split('T')[0];
        setFormData(prev => ({
          ...prev,
          reminder_date: formattedDate
        }));
      }
    }
  };

  const handleBack = () => {
    navigate('/admin/inquiries');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    // Validate mobile number
    if (!/^[0-9]{10}$/.test(formData.mobile_no)) {
      setError('Mobile number must be 10 digits');
      setSaving(false);
      return;
    }

    try {
      const response = await api.post('/inquiry/update_inquiry.php', {
        id: id,
        ...formData
      });
      
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/admin/inquiries');
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to update inquiry');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="inquiry-page">
        <div className="loading">Loading inquiry...</div>
      </div>
    );
  }

  return (
    <div className="inquiry-page">
      <div className="inquiry-container">
        <div className="inquiry-header">
          <div className="inquiry-title-section">
            <button className="back-button" onClick={handleBack} title="Go back (ESC)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h2>Edit Inquiry</h2>
              <p>Update inquiry details</p>
            </div>
          </div>
          
          {/* Status Badge */}
          <div className={`status-badge status-${formData.status.toLowerCase().replace(' ', '-')}`}>
            {formData.status}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="inquiry-form">
          {error && <div className="error-alert">{error}</div>}
          {success && <div className="success-alert">Inquiry updated successfully!</div>}

          <div className="form-group">
            <label htmlFor="full_name">
              Full Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="Enter full name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="mobile_no">
              Mobile No. <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="mobile_no"
              name="mobile_no"
              value={formData.mobile_no}
              onChange={handleChange}
              placeholder="Enter mobile number"
              maxLength="10"
              pattern="[0-9]{10}"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="area">Area</label>
            <input
              type="text"
              id="area"
              name="area"
              value={formData.area}
              onChange={handleChange}
              placeholder="Enter area"
            />
          </div>

          <div className="form-group">
            <label htmlFor="date">
              Date <span className="required">*</span>
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="plan_to_join_days">
              Plan to Join in days
              <span className="field-info"> (Auto-calculates Reminder Date)</span>
            </label>
            <input
              type="number"
              id="plan_to_join_days"
              name="plan_to_join_days"
              value={formData.plan_to_join_days}
              onChange={handleChange}
              placeholder="Enter number of days"
              min="0"
            />
          </div>

          <div className="form-group">
            <label htmlFor="reminder_date">
              Reminder Date
              <span className="auto-calculated"> (Auto-calculated)</span>
            </label>
            <input
              type="date"
              id="reminder_date"
              name="reminder_date"
              value={formData.reminder_date}
              readOnly
              className="readonly-field"
            />
            {formData.reminder_date && (
              <p className="field-helper">
                📅 Reminder set for: {new Date(formData.reminder_date).toLocaleDateString('en-IN', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="source">How you know about Ascent:</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="source"
                  value="Google"
                  checked={formData.source === 'Google'}
                  onChange={handleChange}
                />
                <span>Google</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="source"
                  value="Students Reference"
                  checked={formData.source === 'Students Reference'}
                  onChange={handleChange}
                />
                <span>Students Reference</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="source"
                  value="SignBoard"
                  checked={formData.source === 'SignBoard'}
                  onChange={handleChange}
                />
                <span>SignBoard</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="source"
                  value="Social Media"
                  checked={formData.source === 'Social Media'}
                  onChange={handleChange}
                />
                <span>Social Media</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="source"
                  value="Other"
                  checked={formData.source === 'Other'}
                  onChange={handleChange}
                />
                <span>Other</span>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="status-dropdown"
            >
              <option value="Open">Open</option>
              <option value="Contacted">Contacted</option>
              <option value="Interested">Interested</option>
              <option value="Not Interested">Not Interested</option>
              <option value="Converted">Converted</option>
              <option value="Closed">Closed</option>
            </select>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleBack}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-save"
              disabled={saving || success}
            >
              {saving ? 'Updating...' : success ? 'Updated!' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditInquiry;
